import { useEffect, useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const OBJECT_ID_RE = /^[0-9a-fA-F]{24}$/;

const EXAMPLE_OID = "507f1f77bcf86cd799439011";

// Accept the common wrapping formats people paste from the Mongo shell,
// Compass, driver logs and JSON output — e.g. ObjectId("…"), "…", '…',
// {$oid: "…"} — and recover the inner 24-hex string. Anything we don't
// recognise is returned trimmed and left for the strict regex to reject.
const normalizeObjectIdInput = (raw) => {
  let s = raw.trim();
  if (!s) return s;
  // ObjectId("…") / ObjectID('…') — case-insensitive, quotes optional.
  const wrapped = s.match(/^Object[Ii][Dd]\(\s*["']?([0-9a-fA-F]{24})["']?\s*\)$/);
  if (wrapped) return wrapped[1];
  // Extended JSON: {"$oid": "…"} or {$oid: '…'}
  const extJson = s.match(/^\{\s*["']?\$oid["']?\s*:\s*["']([0-9a-fA-F]{24})["']\s*\}$/);
  if (extJson) return extJson[1];
  // Strip trailing separators that come from copying a shell row.
  s = s.replace(/[,;]+$/, "").trim();
  // Strip matching surrounding quotes (double, single or backtick).
  const quoted = s.match(/^(["'`])([\s\S]*)\1$/);
  if (quoted) s = quoted[2].trim();
  return s;
};

const toHex = (bytes) =>
  Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

const parseObjectId = (raw) => {
  const s = normalizeObjectIdInput(raw);
  if (!s) return null;
  if (!OBJECT_ID_RE.test(s)) return { error: "invalid_format" };
  const lower = s.toLowerCase();
  const bytes = new Uint8Array(12);
  for (let i = 0; i < 12; i += 1) {
    bytes[i] = parseInt(lower.slice(i * 2, i * 2 + 2), 16);
  }
  const timestampSec =
    (bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3];
  const timestampUnsigned = timestampSec >>> 0;
  const randomHex = toHex(bytes.slice(4, 9));
  const counterHex = toHex(bytes.slice(9, 12));
  const counterDec = (bytes[9] << 16) | (bytes[10] << 8) | bytes[11];
  return {
    hex: lower,
    timestampSec: timestampUnsigned,
    randomHex,
    counterHex,
    counterDec,
  };
};

const formatRelative = (deltaSeconds, t) => {
  const abs = Math.abs(deltaSeconds);
  let value;
  let unitKey;
  if (abs < 60) {
    value = abs;
    unitKey = value === 1 ? "mongo_oid_unit_second" : "mongo_oid_unit_seconds";
  } else if (abs < 3600) {
    value = Math.floor(abs / 60);
    unitKey = value === 1 ? "mongo_oid_unit_minute" : "mongo_oid_unit_minutes";
  } else if (abs < 86400) {
    value = Math.floor(abs / 3600);
    unitKey = value === 1 ? "mongo_oid_unit_hour" : "mongo_oid_unit_hours";
  } else if (abs < 2592000) {
    value = Math.floor(abs / 86400);
    unitKey = value === 1 ? "mongo_oid_unit_day" : "mongo_oid_unit_days";
  } else if (abs < 31536000) {
    value = Math.floor(abs / 2592000);
    unitKey = value === 1 ? "mongo_oid_unit_month" : "mongo_oid_unit_months";
  } else {
    value = Math.floor(abs / 31536000);
    unitKey = value === 1 ? "mongo_oid_unit_year" : "mongo_oid_unit_years";
  }
  const unit = t(unitKey);
  const key =
    deltaSeconds >= 0 ? "mongo_oid_relative_ago" : "mongo_oid_relative_in";
  return t(key, { value, unit });
};

// Build a 24-hex-char ObjectId from a Date. The first 4 bytes are the
// second-precision timestamp; the trailing 8 bytes are zeroed so the ID
// sorts as the *earliest possible* value for that instant. This is the
// idiomatic construction for range queries like {_id: {$gte: ObjectId(...)}}.
const objectIdFromDate = (date) => {
  const sec = Math.floor(date.getTime() / 1000);
  if (!Number.isFinite(sec) || sec < 0 || sec > 0xffffffff) return null;
  const hex = sec.toString(16).padStart(8, "0");
  return `${hex}0000000000000000`;
};

const toDatetimeLocalValue = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

const MongoObjectIdInspector = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [dateInput, setDateInput] = useState(() =>
    toDatetimeLocalValue(new Date())
  );
  const [, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => (n + 1) % 1_000_000), 30_000);
    return () => clearInterval(id);
  }, []);

  const parsed = useMemo(() => parseObjectId(input), [input]);

  const generatedFromDate = useMemo(() => {
    if (!dateInput) return null;
    const d = new Date(dateInput);
    if (Number.isNaN(d.getTime())) return null;
    return objectIdFromDate(d);
  }, [dateInput]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const timestampInfo = useMemo(() => {
    if (!parsed || parsed.error) return null;
    const ms = parsed.timestampSec * 1000;
    const date = new Date(ms);
    const nowSec = Math.floor(Date.now() / 1000);
    const delta = nowSec - parsed.timestampSec;
    return {
      unix: parsed.timestampSec,
      iso: date.toISOString(),
      local: date.toLocaleString(),
      relative: formatRelative(delta, t),
    };
  }, [parsed, t]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("mongo_oid_title")}
            </h1>
            <p className="text-gray-600">{t("mongo_oid_intro")}</p>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="mongo-oid-input"
                className="text-sm font-medium text-gray-700"
              >
                {t("mongo_oid_input_label")}
              </label>
              <input
                id="mongo-oid-input"
                type="text"
                spellCheck={false}
                autoComplete="off"
                className="w-full p-3 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                placeholder={t("mongo_oid_input_placeholder")}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                aria-invalid={parsed?.error ? "true" : "false"}
                aria-describedby="mongo-oid-input-hint"
              />
              <p id="mongo-oid-input-hint" className="text-xs text-gray-500">
                {t("mongo_oid_input_accepts_wrapped")}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <StandardButton
                onClick={() => setInput(EXAMPLE_OID)}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("mongo_oid_load_example")}
              </StandardButton>
              <StandardButton
                onClick={() => setInput("")}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("mongo_oid_clear")}
              </StandardButton>
              <StandardButton
                onClick={async () => {
                  try {
                    const text = await navigator.clipboard.readText();
                    setInput(text);
                  } catch {
                    toast.error(t("failed_to_copy"));
                  }
                }}
                type="secondary"
                variant="outlined"
                size="small"
              >
                {t("mongo_oid_paste")}
              </StandardButton>
            </div>

            {parsed?.error && (
              <div
                role="alert"
                className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md"
              >
                {t("mongo_oid_invalid_format")}
              </div>
            )}

            {parsed && !parsed.error && (
              <>
                <div
                  role="status"
                  aria-live="polite"
                  className="bg-green-50 border border-green-200 text-green-800 p-3 rounded-md"
                >
                  <div className="font-medium">
                    {t("mongo_oid_created")} {timestampInfo.relative}
                  </div>
                  <div className="text-sm opacity-80 mt-1">
                    {t("mongo_oid_local")}: {timestampInfo.local}
                  </div>
                  <div className="text-sm opacity-80">
                    {t("mongo_oid_iso_utc")}: {timestampInfo.iso}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                    <div className="flex items-center justify-between mb-2">
                      <strong className="text-indigo-700 text-sm">
                        {t("mongo_oid_field_timestamp")}
                      </strong>
                      <StandardButton
                        onClick={() =>
                          copy(
                            String(parsed.timestampSec),
                            t("mongo_oid_field_timestamp")
                          )
                        }
                        type="secondary"
                        variant="outlined"
                        size="small"
                      >
                        {t("copy_button")}
                      </StandardButton>
                    </div>
                    <p className="font-mono text-sm break-all text-gray-800">
                      {parsed.timestampSec}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {t("mongo_oid_field_timestamp_hint")}
                    </p>
                  </div>

                  <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                    <div className="flex items-center justify-between mb-2">
                      <strong className="text-indigo-700 text-sm">
                        {t("mongo_oid_field_random")}
                      </strong>
                      <StandardButton
                        onClick={() =>
                          copy(parsed.randomHex, t("mongo_oid_field_random"))
                        }
                        type="secondary"
                        variant="outlined"
                        size="small"
                      >
                        {t("copy_button")}
                      </StandardButton>
                    </div>
                    <p className="font-mono text-sm break-all text-gray-800">
                      {parsed.randomHex}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {t("mongo_oid_field_random_hint")}
                    </p>
                  </div>

                  <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
                    <div className="flex items-center justify-between mb-2">
                      <strong className="text-indigo-700 text-sm">
                        {t("mongo_oid_field_counter")}
                      </strong>
                      <StandardButton
                        onClick={() =>
                          copy(parsed.counterHex, t("mongo_oid_field_counter"))
                        }
                        type="secondary"
                        variant="outlined"
                        size="small"
                      >
                        {t("copy_button")}
                      </StandardButton>
                    </div>
                    <p className="font-mono text-sm break-all text-gray-800">
                      {parsed.counterHex}{" "}
                      <span className="text-gray-500">
                        ({parsed.counterDec})
                      </span>
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      {t("mongo_oid_field_counter_hint")}
                    </p>
                  </div>
                </div>
              </>
            )}

            <div className="border-t border-gray-200 pt-4 mt-2">
              <h2 className="text-lg font-semibold text-indigo-700">
                {t("mongo_oid_range_title")}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {t("mongo_oid_range_intro")}
              </p>

              <div className="flex flex-col sm:flex-row sm:items-end gap-3 mt-3">
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="mongo-oid-date"
                    className="text-sm font-medium text-gray-700"
                  >
                    {t("mongo_oid_range_date_label")}
                  </label>
                  <input
                    id="mongo-oid-date"
                    type="datetime-local"
                    value={dateInput}
                    onChange={(e) => setDateInput(e.target.value)}
                    className="p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                  />
                </div>
                <StandardButton
                  onClick={() => setDateInput(toDatetimeLocalValue(new Date()))}
                  type="secondary"
                  variant="outlined"
                  size="small"
                >
                  {t("mongo_oid_range_now")}
                </StandardButton>
              </div>

              {generatedFromDate && (
                <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 mt-3">
                  <div className="flex items-center justify-between mb-2">
                    <strong className="text-indigo-700 text-sm">
                      {t("mongo_oid_range_result")}
                    </strong>
                    <div className="flex gap-2">
                      <StandardButton
                        onClick={() =>
                          copy(generatedFromDate, t("mongo_oid_range_result"))
                        }
                        type="secondary"
                        variant="outlined"
                        size="small"
                      >
                        {t("copy_button")}
                      </StandardButton>
                      <StandardButton
                        onClick={() => setInput(generatedFromDate)}
                        type="secondary"
                        variant="outlined"
                        size="small"
                      >
                        {t("mongo_oid_range_decode")}
                      </StandardButton>
                    </div>
                  </div>
                  <p className="font-mono text-sm break-all text-gray-800">
                    {generatedFromDate}
                  </p>
                  <p className="mt-2 text-xs text-gray-500">
                    {t("mongo_oid_range_hint")}
                  </p>
                </div>
              )}
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("mongo_oid_note_format")}</p>
              <p>{t("mongo_oid_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default MongoObjectIdInspector;
